import {
  Alert,
  AppShell,
  Button,
  Card,
  Center,
  Flex,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useDeleteArtistMutation } from '@/features/admin-panel/api/deleteArtist/hook';
import { useGetArtistsActivityListQuery } from '@/features/admin-panel/api/getArtistsActivitiList/hook';
import { useCreateConversationMutation } from '@/features/chat/api';
import { useChatStore } from '@/features/chat/store/chatStore';
import { ArtistFullProfile } from '@/features/work/view/artist-full-profile/ArtistFullProfile';

export const AdminArtistsList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useGetArtistsActivityListQuery({
    pageNumber: page.toString()
  });

  /* ──────────────────────────────────────────────
     ↓ NEW: данные для обратной нумерации ↓
  ────────────────────────────────────────────── */
  const pageSize = data?.data.pageSize ?? 10;
  const totalPages = data?.data.totalPages ?? 0;
  const totalCount = data?.data.totalCount ?? totalPages * pageSize;
  /* ────────────────────────────────────────────── */

  const { chatProfile } = useChatStore();
  const createConversation = useCreateConversationMutation();
  const deleteArtist = useDeleteArtistMutation();

  // Profile Modal
  const [isProfileOpen, { open: openProfile, close: closeProfile }] =
    useDisclosure();
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);

  // Delete Confirmation Modal
  const [isDeleteOpen, { open: openDelete, close: closeDelete }] =
    useDisclosure();
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');

  const handleConversation = async (senderId: string, recipientId: string) => {
    const response = await createConversation.mutateAsync({
      params: {
        senderProfileId: senderId,
        recipientProfileId: recipientId,
        isDirect: true
      }
    });
    const convId = response.data.conversationId.toString();
    navigate({
      to: '/admin/chat/conversation/$conversationId',
      params: { conversationId: convId }
    });
  };

  const onDeleteClick = (profileId: string) => {
    setDeleteProfileId(profileId);
    setConfirmInput('');
    openDelete();
  };

  const confirmDelete = async () => {
    if (!deleteProfileId) return;
    await deleteArtist.mutateAsync({ profileId: deleteProfileId });
    closeDelete();
    refetch();
  };

  return (
    <AppShell.Main>
      {/* Artist Profile Modal */}
      <Modal
        withCloseButton
        opened={isProfileOpen}
        overlayProps={{ blur: 8, opacity: 0.6 }}
        onClose={closeProfile}
      >
        {currentProfileId && (
          <ScrollArea p='md'>
            <ArtistFullProfile artistProfileId={currentProfileId} />
          </ScrollArea>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        withCloseButton
        opened={isDeleteOpen}
        overlayProps={{ blur: 8, opacity: 0.6 }}
        title='Удаление артиста'
        onClose={closeDelete}
      >
        <Text mb='sm'>
          Вы уверены, что хотите удалить этого артиста? Это действие необратимо.
          Введите &quot;delete&quot; для подтверждения.
        </Text>
        <TextInput
          placeholder='Введите delete для подтверждения'
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.currentTarget.value)}
        />
        <Flex gap='xs' justify='end' mt='md'>
          <Button variant='default' onClick={closeDelete}>
            Отмена
          </Button>
          <Button
            color='red'
            disabled={confirmInput !== 'delete'}
            onClick={confirmDelete}
          >
            Удалить
          </Button>
        </Flex>
      </Modal>

      {/* Header */}
      <Flex align='center' justify='space-between' mb='lg'>
        <Title order={2}>Артисты</Title>
      </Flex>

      {/* Loading, Error, Empty States */}
      {isLoading && (
        <Center style={{ height: 200 }}>
          <Loader />
        </Center>
      )}
      {isError && (
        <Alert color='red' mb='md' title='Ошибка'>
          Не удалось загрузить список артистов.
        </Alert>
      )}
      {!isLoading && data?.data.items.length === 0 && (
        <Center>
          <Text color='dimmed'>Нет артистов для отображения.</Text>
        </Center>
      )}

      {/* Artists Table */}
      {!isLoading && data && (
        <Card withBorder p='lg' radius='md' shadow='sm'>
          <ScrollArea>
            <Table highlightOnHover striped withColumnBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>No</Table.Th>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Имя</Table.Th>
                  <Table.Th>Фамилия</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Телефон</Table.Th>
                  <Table.Th />
                  {/* Actions */}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.data.items.map((artist, index) => {
                  /* ↓ NEW: расчёт обратного индекса ↓ */
                  const descendingIndex =
                    totalCount - ((page - 1) * pageSize + index);
                  return (
                    <Table.Tr key={artist.artistProfileId}>
                      <Table.Td>{descendingIndex}</Table.Td>
                      <Table.Td>{artist.artistProfileId ?? '-'}</Table.Td>
                      <Table.Td>{artist.firstName ?? '-'}</Table.Td>
                      <Table.Td>{artist.lastName ?? '-'}</Table.Td>
                      <Table.Td>{artist.email ?? '-'}</Table.Td>
                      <Table.Td>{artist.phoneNumber ?? '-'}</Table.Td>
                      <Table.Td>
                        <Flex gap='xs' justify='center'>
                          <Button
                            size='xs'
                            variant='outline'
                            onClick={() => {
                              setCurrentProfileId(
                                artist.artistProfileId.toString()
                              );
                              openProfile();
                            }}
                          >
                            Профиль
                          </Button>
                          <Button
                            size='xs'
                            variant='filled'
                            onClick={() =>
                              handleConversation(
                                artist.chatProfileId.toString(),
                                chatProfile?.chatProfileId.toString()!
                              )
                            }
                          >
                            Чат
                          </Button>
                          <Button
                            color='red'
                            size='xs'
                            variant='outline'
                            onClick={() =>
                              onDeleteClick(artist.artistProfileId.toString())
                            }
                          >
                            Удалить
                          </Button>
                        </Flex>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Flex justify='center' mt='md'>
            <Pagination
              boundaries={1}
              siblings={1}
              total={data.data.totalPages}
              value={page}
              onChange={setPage}
            />
          </Flex>
        </Card>
      )}
    </AppShell.Main>
  );
};
